import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, History, Trash2 } from "lucide-react";
import { useStore } from "@/lib/store";
import PosterCard from "@/components/PosterCard";
import MovieModal from "@/components/MovieModal";
import SeriesModal from "@/components/SeriesModal";

const TABS = [["", "All"], ["live", "Live TV"], ["movie", "Movies"], ["series", "Series"]];

export default function Collection({ mode }) {
  const { favs, recents, toggleFav, isFav, clearRecents, pct } = useStore();
  const nav = useNavigate();
  const [tab, setTab] = useState("");
  const [open, setOpen] = useState(null);
  const src = mode === "favorites" ? favs : recents;
  const list = useMemo(() => src.filter((x) => !tab || x.type === tab), [src, tab]);
  const Icon = mode === "favorites" ? Heart : History;

  const openItem = (x) => (x.type === "live" ? nav("/live", { state: { channel: x } }) : setOpen(x));

  return (
    <div className="h-full overflow-y-auto xc-scroll" data-testid={`${mode}-page`}>
      <div className="px-10 pt-10 pb-6 flex items-end gap-6 flex-wrap">
        <div className="flex-1">
          <div className="xc-label">Your library</div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight">{mode === "favorites" ? "Favourites" : "Recently watched"}</h1>
        </div>
        <div className="flex gap-2">
          {TABS.map(([k, l]) => (
            <button
              key={k || "all"}
              onClick={() => setTab(k)}
              className={`rounded-full px-4 py-1.5 text-sm border transition-colors ${tab === k ? "bg-white text-black border-white" : "border-[#212738] text-slate-400 hover:text-white"}`}
              data-testid={`${mode}-tab-${k || "all"}`}
            >
              {l}
            </button>
          ))}
          {mode === "recent" && recents.length > 0 && (
            <button onClick={clearRecents} className="xc-btn-ghost !py-1.5 text-sm" data-testid="recent-clear-button">
              <Trash2 className="w-3.5 h-3.5" /> Clear
            </button>
          )}
        </div>
      </div>
      <div className="px-10 pb-10">
        {!list.length && (
          <div className="rounded-2xl border border-dashed border-[#212738] py-20 text-center" data-testid={`${mode}-empty`}>
            <Icon className="w-10 h-10 mx-auto text-[#38425C]" />
            <p className="font-heading text-xl mt-4">{mode === "favorites" ? "No favourites yet" : "Nothing watched yet"}</p>
            <p className="text-sm text-slate-500 mt-1">{mode === "favorites" ? "Tap the heart on any channel, movie or series." : "Channels and titles you play will show up here."}</p>
          </div>
        )}
        <div className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-5">
          {list.map((x) => (
            <PosterCard
              key={`${x.type}-${x.id}`}
              title={x.name}
              image={x.icon}
              rating={x.rating}
              sub={x.type === "live" ? "Live channel" : x.sub || (x.type === "movie" ? "Movie" : "Series")}
              progress={pct(`${x.type}:${x.id}`)}
              fav={isFav(x.type, x.id)}
              onFav={() => toggleFav(x)}
              onClick={() => openItem(x)}
              testId={`${mode}-item-${x.type}-${x.id}`}
            />
          ))}
        </div>
      </div>
      {open?.type === "movie" && <MovieModal movie={open} onClose={() => setOpen(null)} />}
      {open?.type === "series" && <SeriesModal series={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
